import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, SlidersHorizontal, ShieldCheck, ArrowLeftRight, CheckCircle2, Activity,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Sparkline from '../components/Sparkline';
import '../assets/css/landing.css';

/* prefers-reduced-motion hook — relay/ticker fall back to static state */
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

/* ── Signature: the signal relay ───────────────────────────────
   One trader node emits a trade; pulses travel down the wires and
   light up each copier node in turn. SMIL pulses are only mounted
   when motion is allowed; otherwise the wires render lit + static. */
function SignalRelay({ reduced }) {
  const copiers = [
    { y: 46,  label: 'You',      lot: '0.40' },
    { y: 118, label: 'Copier',   lot: '0.10' },
    { y: 190, label: 'Copier',   lot: '1.00' },
    { y: 262, label: 'Copier',   lot: '0.25' },
  ];
  const tx = 96, ty = 154;        // trader node center
  const cx = 366;                 // copier column x

  return (
    <svg className="relay" viewBox="0 0 460 308" role="img"
         aria-label="A trader's position being mirrored to four copier accounts">
      <defs>
        <linearGradient id="wire" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#F5B301" stopOpacity="0.15" />
          <stop offset="1" stopColor="#F5B301" stopOpacity="0.55" />
        </linearGradient>
        <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {copiers.map((c, i) => {
        const d = `M ${tx} ${ty} C ${tx + 120} ${ty}, ${cx - 120} ${c.y + 18}, ${cx} ${c.y + 18}`;
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="url(#wire)" strokeWidth="1.6" />
            {!reduced && (
              <circle r="3.6" fill="#C6F135" filter="url(#glow)">
                <animateMotion dur="2.6s" begin={`${i * 0.5}s`} repeatCount="indefinite"
                               path={d} keyPoints="0;1" keyTimes="0;1" calcMode="spline"
                               keySplines="0.4 0 0.2 1" />
                <animate attributeName="opacity" dur="2.6s" begin={`${i * 0.5}s`}
                         repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.1;0.85;1" />
              </circle>
            )}
          </g>
        );
      })}

      {/* trader node */}
      <g className="relay-node relay-trader">
        <rect x="40" y="128" width="112" height="52" rx="10" />
        <text x="96" y="150" className="relay-node-tt">EURUSD · BUY</text>
        <text x="96" y="168" className="relay-node-lot">1.00 lot</text>
      </g>

      {/* copier nodes */}
      {copiers.map((c, i) => (
        <g key={i} className={`relay-node relay-copier ${reduced ? 'lit' : ''}`}
           style={{ '--d': `${i * 0.5 + 0.9}s` }}>
          <rect x={cx - 4} y={c.y} width="98" height="40" rx="9" />
          <text x={cx + 45} y={c.y + 17} className="relay-node-lbl">{c.label}</text>
          <text x={cx + 45} y={c.y + 31} className="relay-node-lot">{c.lot} lot</text>
        </g>
      ))}
    </svg>
  );
}

const TICKER = [
  ['EURUSD', '1.0842', 0.21], ['XAUUSD', '2,318.40', 0.64], ['BTCUSD', '67,910', -0.83],
  ['US30', '38,142', 0.12], ['GBPJPY', '198.04', -0.34], ['USDJPY', '156.21', 0.18],
  ['NAS100', '18,330', 0.47], ['XAGUSD', '29.84', -0.22], ['ETHUSD', '3,512', 1.04],
];

const TRADERS = [
  { name: 'Apex_FX',     win: 71, copiers: 412, ret: '+38.2%', spark: [4,5,4.6,6,5.4,7,6.8,8.1,7.6,9.2] },
  { name: 'GoldRunner',  win: 64, copiers: 287, ret: '+24.9%', spark: [5,4.7,5.3,5,6,5.6,6.4,6.1,7,7.3] },
  { name: 'SteadyPips',  win: 69, copiers: 533, ret: '+19.4%', spark: [3,3.4,3.2,3.9,4.1,4,4.6,4.9,5.2,5.5] },
];

const FEATURES = [
  { k: 'mirror', Icon: Zap,             t: 'Millisecond mirroring', d: 'A bridge polls each trader 20× a second. Opens, modifies, and closes reach your broker in well under a second.' },
  { k: 'scale',  Icon: SlidersHorizontal, t: 'Four scaling modes',  d: 'Copy by ratio, fixed lot, exact mirror, or equity percentage — clamped to your own min and max lot size.' },
  { k: 'risk',   Icon: ShieldCheck,     t: 'Risk guardrails',       d: 'Daily-loss limit, max drawdown, free-margin floor, and open-trade caps pause copying before a bad day compounds.' },
  { k: 'symbol', Icon: ArrowLeftRight,  t: 'Cross-broker symbols',  d: 'Trader on Exness, you on Deriv? Map BTCUSDc → BTCUSD per relationship so names never break a copy.' },
  { k: 'close',  Icon: CheckCircle2,    t: 'Confirmed closes',      d: 'Every close is verified with your broker and retried until it lands — a position is never left open by accident.' },
  { k: 'live',   Icon: Activity,        t: 'Live dashboard & alerts', d: 'Watch equity, open trades, and copy executions update in real time, with email and in-app notifications.' },
];

export default function Landing() {
  const { user } = useAuth();
  const reduced = useReducedMotion();
  const yearRef = useRef(new Date().getFullYear());

  return (
    <div className="lp">
      {/* ── Nav ─────────────────────────────────────────────── */}
      <header className="lp-nav">
        <div className="lp-shell lp-nav-inner">
          <Link to="/" className="lp-brand">
            <span className="lp-mark" aria-hidden="true" />
            CopyTrade
          </Link>
          <nav className="lp-nav-links">
            <a href="#how">How it works</a>
            <a href="#traders">Traders</a>
            <a href="#features">Features</a>
            <a href="#security">Security</a>
          </nav>
          <div className="lp-nav-cta">
            {user ? (
              <Link to={`/${user.role}`} className="lp-btn lp-btn-primary">Go to dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="lp-btn lp-btn-ghost">Sign in</Link>
                <Link to="/register" className="lp-btn lp-btn-primary">Join free</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-shell lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">MT5 copy trading</span>
            <h1 className="lp-h1">Mirror the floor.</h1>
            <p className="lp-lede">
              Follow proven MT5 traders and copy every position straight to your own
              broker account — automatically, in milliseconds, with risk limits you set.
            </p>
            <div className="lp-hero-actions">
              <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">Start copying</Link>
              <Link to="/register" className="lp-btn lp-btn-outline lp-btn-lg">Become a trader</Link>
            </div>
            <dl className="lp-hero-stats">
              <div><dt>Median copy</dt><dd className="mono">&lt;1s</dd></div>
              <div><dt>Scaling modes</dt><dd className="mono">4</dd></div>
              <div><dt>Credentials</dt><dd className="mono">AES-256</dd></div>
            </dl>
          </div>

          <div className="lp-hero-relay">
            <div className="lp-relay-caption">
              <span className="dot" /> Live signal relay
            </div>
            <SignalRelay reduced={reduced} />
          </div>
        </div>
      </section>

      {/* ── Ticker ──────────────────────────────────────────── */}
      <div className="lp-ticker" aria-hidden="true">
        <div className={`lp-ticker-track ${reduced ? 'is-static' : ''}`}>
          {[...TICKER, ...TICKER].map(([s, p, d], i) => (
            <span className="lp-tick" key={i}>
              <span className="sym">{s}</span>
              <span className="px mono">{p}</span>
              <span className={`chg ${d >= 0 ? 'up' : 'down'} mono`}>
                {d >= 0 ? '▲' : '▼'} {Math.abs(d).toFixed(2)}%
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* ── How it works (a real sequence → numbered) ──────── */}
      <section className="lp-section" id="how">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">How it works</span>
            <h2 className="lp-h2">From their screen to yours, in three steps.</h2>
          </header>
          <ol className="lp-steps">
            <li>
              <span className="lp-step-no mono">01</span>
              <h3>Choose a trader</h3>
              <p>Browse live performance — win rate, drawdown, return, and follower count — and follow the ones you trust.</p>
            </li>
            <li>
              <span className="lp-step-no mono">02</span>
              <h3>We mirror every trade</h3>
              <p>The moment they open, modify, or close a position, the copy engine reproduces it on your broker, scaled to your account.</p>
            </li>
            <li>
              <span className="lp-step-no mono">03</span>
              <h3>You stay in control</h3>
              <p>Set lot scaling, drawdown caps, and per-symbol rules. Pause, resume, or unfollow whenever you want.</p>
            </li>
          </ol>
        </div>
      </section>

      {/* ── Traders showcase ────────────────────────────────── */}
      <section className="lp-section lp-section-alt" id="traders">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">The leaderboard</span>
            <h2 className="lp-h2">Copy the traders who actually win.</h2>
            <p className="lp-sec-sub">Every trader is ranked on verified, broker-side results. Here's a sample.</p>
          </header>
          <div className="lp-trader-grid">
            {TRADERS.map(t => (
              <article className="lp-trader" key={t.name}>
                <div className="lp-trader-top">
                  <div className="lp-avatar mono">{t.name.slice(0, 2).toUpperCase()}</div>
                  <div className="lp-trader-id">
                    <div className="name">{t.name}</div>
                    <div className="copiers mono">{t.copiers} copiers</div>
                  </div>
                  <div className="lp-trader-ret mono">{t.ret}</div>
                </div>
                <Sparkline values={t.spark} height={44} stroke="#22C55E" />
                <div className="lp-trader-stats">
                  <div><span className="lbl">Win rate</span><span className="val mono">{t.win}%</span></div>
                  <div><span className="lbl">30-day</span><span className="val mono">{t.ret}</span></div>
                </div>
                <Link to="/register" className="lp-btn lp-btn-outline lp-btn-block">Copy {t.name}</Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section className="lp-section" id="features">
        <div className="lp-shell">
          <header className="lp-sec-head">
            <span className="lp-eyebrow">Under the hood</span>
            <h2 className="lp-h2">Built for the messy reality of live trading.</h2>
          </header>
          <div className="lp-feature-grid">
            {FEATURES.map(({ k, Icon, t, d }) => (
              <article className="lp-feature" key={k}>
                <span className="lp-feature-glyph" aria-hidden="true">
                  <Icon size={20} strokeWidth={2} />
                </span>
                <h3>{t}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Two paths ────────────────────────────────────────── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-shell lp-paths">
          <article className="lp-path">
            <span className="lp-eyebrow">For copiers</span>
            <h3>Trade like a pro without being one.</h3>
            <p>Put your account on autopilot behind traders with a track record. Keep full control of risk and walk away anytime.</p>
            <Link to="/register" className="lp-btn lp-btn-primary">Start copying</Link>
          </article>
          <article className="lp-path">
            <span className="lp-eyebrow">For traders</span>
            <h3>Turn your edge into a following.</h3>
            <p>Trade your own MT5 account as usual. Every position you take is mirrored to your copiers — no extra work, no signal groups.</p>
            <Link to="/register" className="lp-btn lp-btn-outline">Become a trader</Link>
          </article>
        </div>
      </section>

      {/* ── Security ──────────────────────────────────────────── */}
      <section className="lp-section" id="security">
        <div className="lp-shell lp-security">
          <header className="lp-sec-head lp-sec-head-left">
            <span className="lp-eyebrow">Security</span>
            <h2 className="lp-h2">Your credentials never sit in the open.</h2>
          </header>
          <ul className="lp-sec-list">
            <li><strong>AES-256 encryption.</strong> MT5 passwords are encrypted at rest and never returned by the API.</li>
            <li><strong>Isolated containers.</strong> Every account runs in its own sandboxed MetaTrader instance — no shared sessions.</li>
            <li><strong>Token auth.</strong> Short-lived access tokens with refresh rotation, invalidated the moment you log out.</li>
            <li><strong>You hold the brakes.</strong> Risk limits, pause, and unfollow are yours — copying stops the instant you say so.</li>
          </ul>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────── */}
      <section className="lp-cta">
        <div className="lp-shell lp-cta-inner">
          <h2 className="lp-h2">Ready to mirror the floor?</h2>
          <p>Create an account, connect your broker, and start copying in minutes.</p>
          <div className="lp-hero-actions">
            <Link to="/register" className="lp-btn lp-btn-primary lp-btn-lg">Create your account</Link>
            <Link to="/login" className="lp-btn lp-btn-ghost lp-btn-lg">Sign in</Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-shell lp-footer-inner">
          <Link to="/" className="lp-brand"><span className="lp-mark" aria-hidden="true" />CopyTrade</Link>
          <p className="lp-foot-note">
            Trading involves substantial risk. Past performance is not indicative of future results.
            Copy at your own discretion.
          </p>
          <span className="lp-foot-copy mono">© {yearRef.current}</span>
        </div>
      </footer>
    </div>
  );
}
